import React, { useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { DatePicker } from 'rsuite';
import { Container } from 'react-bootstrap';
import { format, parseISO, isAfter, isValid, subDays, subMonths, subYears, startOfDay } from 'date-fns';
import { RootState } from '../services/rootReducer';
import { setDateIntervalFrom, setDateIntervalTo } from '../services/View_Slice';
import styles from './css/DateIntervalPicker.module.css';
import 'rsuite/dist/styles/rsuite-default.css';

type DateIntervalPickerProps = {
    onFilterApplied?: () => void;
};

function DateIntervalPicker(props: DateIntervalPickerProps) {
    const dispatch = useDispatch();

    const { dateIntervalFrom, dateIntervalTo } = useSelector(
        (state: RootState) => ({
            dateIntervalFrom: state.View.dateIntervalFrom,
            dateIntervalTo: state.View.dateIntervalTo,
        }),
        shallowEqual
    );

    const [fromDate, setFromDate] = useState<Date | undefined>(dateIntervalFrom ? parseISO(dateIntervalFrom) : undefined);
    const [toDate, setToDate] = useState<Date | undefined>(dateIntervalTo ? parseISO(dateIntervalTo) : undefined);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Validate date range (basic validation only)
    const validateDateRange = (from: Date | undefined, to: Date | undefined): string | null => {
        if (!from || !to) {
            return null; // No validation error if dates are not selected
        }

        if (!isValid(from) || !isValid(to)) {
            return 'Invalid date format';
        }

        if (isAfter(from, to)) {
            return 'Start date must be before end date';
        }

        return null;
    };

    const handleFromDateChange = (value: Date | null | undefined) => {
        const dateValue = value ?? undefined;
        setFromDate(dateValue);

        if (!dateValue) {
            dispatch(setDateIntervalFrom(null));
            setValidationError(null);
            // Re-query with only toDate if it exists
            const toDateStr = toDate ? format(toDate, 'yyyy-MM-dd') : null;
            applyDateIntervalFilter(null, toDateStr);
            return;
        }

        const error = validateDateRange(dateValue, toDate);
        setValidationError(error);

        if (!error) {
            const dateStr = format(dateValue, 'yyyy-MM-dd');
            dispatch(setDateIntervalFrom(dateStr));

            // Apply filter immediately - works with just from date or both dates
            const toDateStr = toDate ? format(toDate, 'yyyy-MM-dd') : null;
            applyDateIntervalFilter(dateStr, toDateStr);
        }
    };

    const handleToDateChange = (value: Date | null | undefined) => {
        const dateValue = value ?? undefined;
        setToDate(dateValue);

        if (!dateValue) {
            dispatch(setDateIntervalTo(null));
            setValidationError(null);
            // Re-query with only fromDate if it exists
            const fromDateStr = fromDate ? format(fromDate, 'yyyy-MM-dd') : null;
            applyDateIntervalFilter(fromDateStr, null);
            return;
        }

        const error = validateDateRange(fromDate, dateValue);
        setValidationError(error);

        if (!error) {
            const dateStr = format(dateValue, 'yyyy-MM-dd');
            dispatch(setDateIntervalTo(dateStr));

            // Apply filter immediately - works with just to date or both dates
            const fromDateStr = fromDate ? format(fromDate, 'yyyy-MM-dd') : null;
            applyDateIntervalFilter(fromDateStr, dateStr);
        }
    };

    const applyDateIntervalFilter = (from: string | null, to: string | null) => {
        // Query.tsx will handle the query dispatch when dateIntervalFrom/To changes

        if (props.onFilterApplied) {
            props.onFilterApplied();
        }
    };

    const handleClearFrom = () => {
        setFromDate(undefined);
        dispatch(setDateIntervalFrom(null));
        setValidationError(null);

        // Re-query with only toDate if it exists, or no date filter
        const toDateStr = toDate ? format(toDate, 'yyyy-MM-dd') : null;
        applyDateIntervalFilter(null, toDateStr);
    };

    const handleClearTo = () => {
        setToDate(undefined);
        dispatch(setDateIntervalTo(null));
        setValidationError(null);

        // Re-query with only fromDate if it exists, or no date filter
        const fromDateStr = fromDate ? format(fromDate, 'yyyy-MM-dd') : null;
        applyDateIntervalFilter(fromDateStr, null);
    };

    // Define preset date ranges for quick selection
    const today = startOfDay(new Date());
    const dateRanges = [
        {
            label: 'Today',
            value: today,
            closeOverlay: true
        },
        {
            label: 'Yesterday',
            value: subDays(today, 1),
            closeOverlay: true
        },
        {
            label: 'Last Week',
            value: subDays(today, 7),
            closeOverlay: true
        },
        {
            label: 'Last Month',
            value: subMonths(today, 1),
            closeOverlay: true
        },
        {
            label: 'Last 3 Months',
            value: subMonths(today, 3),
            closeOverlay: true
        },
        {
            label: 'Last 6 Months',
            value: subMonths(today, 6),
            closeOverlay: true
        },
        {
            label: 'Last Year',
            value: subYears(today, 1),
            closeOverlay: true
        }
    ];

    return (
        <Container className={styles.container}>
            <div className={styles.label}>Date Interval</div>
            <div className={styles.pickerContainer}>
                <div className={styles.pickerWrapper}>
                    <label className={styles.pickerLabel}>From:</label>
                    <DatePicker
                        value={fromDate}
                        onChange={handleFromDateChange}
                        placeholder="Select start date"
                        format="YYYY-MM-DD"
                        oneTap
                        cleanable
                        onClean={handleClearFrom}
                        block={false}
                        container={() => document.body}
                        placement="bottomStart"
                        ranges={dateRanges}
                    />
                </div>
                <div className={styles.pickerWrapper}>
                    <label className={styles.pickerLabel}>To:</label>
                    <DatePicker
                        value={toDate}
                        onChange={handleToDateChange}
                        placeholder="Select end date"
                        format="YYYY-MM-DD"
                        oneTap
                        cleanable
                        onClean={handleClearTo}
                        block={false}
                        container={() => document.body}
                        placement="bottomEnd"
                        ranges={dateRanges}
                    />
                </div>
            </div>
            {validationError && (
                <div className={styles.errorMessage}>{validationError}</div>
            )}
        </Container>
    );
}

export default React.memo(DateIntervalPicker);
